<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import type {
  TpgChangeEvent,
  TpgChangeEventType,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarLocalLoader,
  GrammarModal,
  GrammarTabBar,
  GrammarTabItem,
} from '@nora-monorepo/grammar-ui';
import type { ReactionResultDTO } from '@nora-monorepo/nora-api-client';
import {
  AvatarImage,
  bgSecondary,
  borderPrimary,
  getInitials,
  HoverableUserName,
  textSecondary,
} from '@nora-monorepo/ui-shared';

import type { MessageState } from '#nora-chat-ui/states';
import { useReactionsModalStore } from '#nora-chat-ui/stores/conversation/message/reactions-modal-store';

interface MessageReactionsProps {
  messageState: MessageState;
  maxReactions?: number;
}

const props = withDefaults(defineProps<MessageReactionsProps>(), {
  maxReactions: 2,
});

enum MessageReactionsTab {
  All = 'all',
}

const reactionModalStore = useReactionsModalStore();
const { fetchReactionsData, reset } = reactionModalStore;
const { reactions, isLoading } = storeToRefs(reactionModalStore);
const panelIsVisible = ref<boolean>(false);
const selectedTab = ref<string>(MessageReactionsTab.All);

function onTabChange(tab: TpgChangeEvent<TpgChangeEventType>) {
  selectedTab.value = tab.value as string;
}

const showReactions = computed(
  () =>
    props.messageState.message.reactions?.length
    && !props.messageState.message.deletedAt,
);

const splitReactions = computed((): Record<string, ReactionResultDTO[]> => {
  if (!reactions.value) {
    return {};
  }

  const unique = [
    ...new Set(reactions.value.map(reaction => reaction.reaction)),
  ];

  const splitReactions: Record<string, ReactionResultDTO[]> = {};

  unique.forEach((reaction) => {
    if (!reactions) {
      return;
    }

    splitReactions[reaction] = reactions.value.filter(
      reactionResultDTO => reactionResultDTO.reaction === reaction,
    );
  });

  return splitReactions;
});

const previewReactions = computed(() => {
  if (!props.messageState.message.reactions) {
    return [];
  }
  return props.messageState.message.reactions.slice(0, props.maxReactions);
});

const totalReactions = computed(() => {
  if (!props.messageState.message.reactions) {
    return 0;
  }
  let total = 0;
  props.messageState.message.reactions.forEach((reaction) => {
    total += reaction.count;
  });
  return total;
});

function toggleReactionsModal(visible?: boolean) {
  panelIsVisible.value = visible ?? !panelIsVisible.value;
  if (panelIsVisible.value) {
    fetchReactionsData(
      props.messageState.message.id,
      props.messageState.message.conversationId,
    );
  }
  else {
    reset();
  }
}
</script>

<template>
  <transition name="fade">
    <div v-if="showReactions" class="-ml-2 -mt-2">
      <div
        :class="[borderPrimary, bgSecondary, textSecondary]"
        class="cursor-pointer min-w-full w-auto whitespace-nowrap px-1 rounded-xl text-sm border"
        @click.stop.prevent="() => toggleReactionsModal(true)"
      >
        <span
          v-for="reaction in previewReactions"
          :key="reaction.reaction"
          class="pr-1 last:pr-0"
        >
          {{ reaction.reaction }}
        </span>
        <span v-if="messageState.message.reactions">{{ totalReactions }}</span>
      </div>
    </div>
  </transition>
  <GrammarModal
    :is-open="panelIsVisible"
    :label="`Reactions (${totalReactions})`"
    :allow-scroll="true"
    @close="() => toggleReactionsModal(false)"
  >
    <template #content>
      <GrammarLocalLoader v-if="isLoading" />
      <div v-else class="min-w-[250px] pb-6">
        <GrammarTabBar
          :selected-value="selectedTab"
          class="px-5"
          justify-content="stretch"
          @change="onTabChange"
        >
          <GrammarTabItem
            :label="`All ${totalReactions}`"
            :value="MessageReactionsTab.All"
          />
          <GrammarTabItem
            v-for="(reactionsOfType, reaction) in splitReactions"
            :key="reaction"
            :label="`${reaction} ${reactionsOfType.length}`"
            :value="reaction"
          />
        </GrammarTabBar>
        <div v-show="selectedTab === MessageReactionsTab.All" class="mt-4">
          <div class="space-y-2">
            <div
              v-for="(reaction, index) in reactions"
              :key="`${reaction.user.id}-${reaction.reaction}-${index}`"
              class="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-sm transition-colors"
            >
              <AvatarImage
                :id="reaction.user.id"
                :text="getInitials(reaction.user)"
              />
              <div class="flex-1 min-w-0">
                <HoverableUserName :user="reaction.user" />
              </div>
              <div class="text-2xl">
                {{ reaction.reaction }}
              </div>
            </div>
          </div>
        </div>
        <div
          v-for="(reactionsOfType, reaction) in splitReactions"
          v-show="selectedTab === reaction"
          :key="reaction"
          class="mt-4"
        >
          <div class="space-y-2">
            <div
              v-for="(reactionItem, index) in reactionsOfType"
              :key="`${reactionItem.user.id}-${reactionItem.reaction}-${index}`"
              class="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-sm transition-colors"
            >
              <AvatarImage
                :id="reactionItem.user.id"
                :text="getInitials(reactionItem.user)"
              />
              <div class="flex-1 min-w-0">
                <HoverableUserName :user="reactionItem.user" />
              </div>
              <div class="text-2xl">
                {{ reactionItem.reaction }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </GrammarModal>
</template>
